import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { idsConHijos, sinTildes } from './organizacionesLogica'
import ComercialChip from './ComercialChip'

// The organizations as a tree (GET /organizaciones/arbol): each branch folds,
// a click on a name opens its card. Used by the Organizaciones page (Árbol view)
// and by a card's Estructura tab (its whole family, itself marked).
//   abiertosIniciales: Set of ids open at first (a new Set starts the tree over: pass a `key`)
//   resaltar: the search text to mark in the names; coincidencias: Set of matching ids
//   actual: the id to mark as "this one"; herramientas: show "Expand all / Collapse all"
export default function ComercialOrganizacionesArbol({
  nodos, abiertosIniciales, resaltar, coincidencias, actual, onAbrir, herramientas,
}) {
  const { t } = useTranslation('comercial')
  const [abiertos, setAbiertos] = useState(() => new Set(abiertosIniciales || []))
  const alternar = (id) => setAbiertos(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <div>
      {herramientas && (
        <div className="flex justify-end gap-3 mb-2 text-xs">
          <button type="button" onClick={() => setAbiertos(idsConHijos(nodos))} className="text-gray-500 hover:text-gray-900 hover:underline">
            {t('arbol.expandirTodo')}
          </button>
          <button type="button" onClick={() => setAbiertos(new Set())} className="text-gray-500 hover:text-gray-900 hover:underline">
            {t('arbol.contraerTodo')}
          </button>
        </div>
      )}
      <ul role="tree" className="text-sm">
        {nodos.map(n => (
          <Nodo key={n.id} nodo={n} nivel={0} abiertos={abiertos} alternar={alternar} resaltar={resaltar}
            coincidencias={coincidencias} actual={actual} onAbrir={onAbrir} />
        ))}
      </ul>
    </div>
  )
}

function Nodo({ nodo, nivel, abiertos, alternar, resaltar, coincidencias, actual, onAbrir }) {
  const { t } = useTranslation('comercial')
  const hijos = nodo.hijos || []
  const abierto = abiertos.has(nodo.id)
  const esActual = nodo.id === actual
  return (
    <li role="treeitem" aria-expanded={hijos.length ? abierto : undefined} aria-current={esActual || undefined}>
      <div className={`flex items-center gap-1 rounded-md pr-2 py-0.5 ${esActual ? 'bg-sky-50 ring-1 ring-inset ring-sky-200' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${nivel * 1.25}rem` }}>
        {hijos.length
          ? <button type="button" onClick={() => alternar(nodo.id)} aria-label={t(abierto ? 'arbol.contraer' : 'arbol.expandir')}
              className="w-6 h-6 shrink-0 rounded text-gray-400 hover:text-gray-800 hover:bg-gray-100 leading-none">
              <span className={`inline-block transition-transform ${abierto ? 'rotate-90' : ''}`}>▸</span>
            </button>
          : <span className="w-6 h-6 shrink-0 inline-flex items-center justify-center text-gray-300" aria-hidden="true">·</span>}
        <button type="button" onClick={() => onAbrir(nodo.id)} disabled={esActual}
          className={`min-w-0 truncate text-left ${esActual ? 'font-semibold text-gray-900 cursor-default'
            : nodo.archivada ? 'text-gray-400 hover:underline' : 'text-gray-800 hover:underline'}
            ${coincidencias?.has(nodo.id) ? 'font-medium' : ''}`}>
          <Resaltado texto={nodo.nombre_comercial} q={resaltar} />
        </button>
        {hijos.length > 0 && <span className="shrink-0 text-xs text-gray-400 tabular-nums">{hijos.length}</span>}
        {nodo.archivada && <ComercialChip variante="archivado">{t('chips.archivada')}</ComercialChip>}
      </div>
      {abierto && hijos.length > 0 && (
        <ul role="group">
          {hijos.map(h => (
            <Nodo key={h.id} nodo={h} nivel={nivel + 1} abiertos={abiertos} alternar={alternar} resaltar={resaltar}
              coincidencias={coincidencias} actual={actual} onAbrir={onAbrir} />
          ))}
        </ul>
      )}
    </li>
  )
}

// The searched text marked in a name (accents and case aside). Only when
// dropping accents keeps the name's length, so the positions still match.
function Resaltado({ texto, q }) {
  const buscado = sinTildes(q).trim()
  const base = sinTildes(texto)
  const i = buscado ? base.indexOf(buscado) : -1
  if (i < 0 || base.length !== texto.length) return texto
  return (
    <>
      {texto.slice(0, i)}
      <mark className="bg-amber-100 text-inherit rounded-sm">{texto.slice(i, i + buscado.length)}</mark>
      {texto.slice(i + buscado.length)}
    </>
  )
}
